import { useState, useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";

interface Country {
  code: string;
  name: string;
  dial: string;
  flag: string;
  pattern: RegExp;
  placeholder: string;
}

const COUNTRIES: Country[] = [
  { code: "AE", name: "UAE", dial: "+971", flag: "🇦🇪", pattern: /^[0-9]{8,9}$/, placeholder: "50 123 4567" },
  { code: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦", pattern: /^[0-9]{8,9}$/, placeholder: "50 123 4567" },
  { code: "BH", name: "Bahrain", dial: "+973", flag: "🇧🇭", pattern: /^[0-9]{7,8}$/, placeholder: "3600 1234" },
  { code: "KW", name: "Kuwait", dial: "+965", flag: "🇰🇼", pattern: /^[0-9]{7,8}$/, placeholder: "500 12345" },
  { code: "OM", name: "Oman", dial: "+968", flag: "🇴🇲", pattern: /^[0-9]{7,8}$/, placeholder: "9200 1234" },
  { code: "QA", name: "Qatar", dial: "+974", flag: "🇶🇦", pattern: /^[0-9]{7,8}$/, placeholder: "3312 3456" },
  { code: "EG", name: "Egypt", dial: "+20", flag: "🇪🇬", pattern: /^[0-9]{9,10}$/, placeholder: "100 123 4567" },
  { code: "JO", name: "Jordan", dial: "+962", flag: "🇯🇴", pattern: /^[0-9]{8,9}$/, placeholder: "79 123 4567" },
  { code: "LB", name: "Lebanon", dial: "+961", flag: "🇱🇧", pattern: /^[0-9]{7,8}$/, placeholder: "71 123 456" },
  { code: "IQ", name: "Iraq", dial: "+964", flag: "🇮🇶", pattern: /^[0-9]{9,10}$/, placeholder: "790 123 4567" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸", pattern: /^[0-9]{10}$/, placeholder: "201 555 0123" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧", pattern: /^[0-9]{10,11}$/, placeholder: "7911 123456" },
  { code: "IN", name: "India", dial: "+91", flag: "🇮🇳", pattern: /^[0-9]{10}$/, placeholder: "98765 43210" },
  { code: "PK", name: "Pakistan", dial: "+92", flag: "🇵🇰", pattern: /^[0-9]{9,10}$/, placeholder: "301 2345678" },
  { code: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭", pattern: /^[0-9]{9,10}$/, placeholder: "917 123 4567" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷", pattern: /^[0-9]{9}$/, placeholder: "6 12 34 56 78" },
  { code: "DE", name: "Germany", dial: "+49", flag: "🇩🇪", pattern: /^[0-9]{10,11}$/, placeholder: "151 2345 6789" },
  { code: "TR", name: "Turkey", dial: "+90", flag: "🇹🇷", pattern: /^[0-9]{10}$/, placeholder: "501 234 5678" },
  { code: "AU", name: "Australia", dial: "+61", flag: "🇦🇺", pattern: /^[0-9]{9}$/, placeholder: "412 345 678" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦", pattern: /^[0-9]{10}$/, placeholder: "204 555 0123" },
];

// Map timezone to likely country
function detectCountry(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzMap: Record<string, string> = {
      "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Bahrain": "BH",
      "Asia/Kuwait": "KW", "Asia/Muscat": "OM", "Asia/Qatar": "QA",
      "Africa/Cairo": "EG", "Asia/Amman": "JO", "Asia/Beirut": "LB",
      "Asia/Baghdad": "IQ", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
      "Asia/Karachi": "PK", "Asia/Manila": "PH", "Europe/London": "GB",
      "Europe/Paris": "FR", "Europe/Berlin": "DE", "Europe/Istanbul": "TR",
      "Australia/Sydney": "AU", "Australia/Melbourne": "AU",
      "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
      "America/Los_Angeles": "US", "America/Toronto": "CA", "America/Vancouver": "CA",
    };
    return tzMap[tz] || "AE";
  } catch {
    return "AE";
  }
}

// Parse a stored full phone string into { countryCode, localNumber }
function parsePhone(full: string): { countryCode: string; localNumber: string } {
  if (!full) return { countryCode: detectCountry(), localNumber: "" };
  // Try matching dial codes longest first
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (full.startsWith(c.dial)) {
      return { countryCode: c.code, localNumber: full.slice(c.dial.length).replace(/\s/g, "") };
    }
  }
  return { countryCode: detectCountry(), localNumber: full.replace(/[^\d]/g, "") };
}

interface PhoneInputProps {
  value: string;
  onChange: (fullPhone: string) => void;
  error?: string;
  className?: string;
}

export function PhoneInput({ value, onChange, error, className }: PhoneInputProps) {
  const parsed = useMemo(() => parsePhone(value), [value]);
  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Sync from parent when value changes externally
  useEffect(() => {
    const p = parsePhone(value);
    setCountryCode(p.countryCode);
    setLocalNumber(p.localNumber);
  }, [value]);

  useEffect(() => {
    if (dropdownOpen) {
      searchRef.current?.focus();
      const handler = (e: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setDropdownOpen(false);
          setSearch("");
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [dropdownOpen]);

  const country = COUNTRIES.find(c => c.code === countryCode) || COUNTRIES[0];

  const emitChange = (code: string, num: string) => {
    const c = COUNTRIES.find(co => co.code === code) || COUNTRIES[0];
    const digits = num.replace(/[^\d]/g, "");
    onChange(digits ? `${c.dial}${digits}` : "");
  };

  const handleCountrySelect = (code: string) => {
    setCountryCode(code);
    setDropdownOpen(false);
    setSearch("");
    emitChange(code, localNumber);
  };

  const handleNumberChange = (val: string) => {
    const digits = val.replace(/[^\d]/g, "");
    setLocalNumber(digits);
    emitChange(countryCode, digits);
  };

  const filtered = COUNTRIES.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.dial.includes(search)
  );

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex gap-0">
        {/* Country selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1 h-12 px-3 rounded-l-md border border-r-0 border-input bg-muted/50 text-sm hover:bg-muted transition-colors min-w-[90px]"
          >
            <span className="text-base">{country.flag}</span>
            <span className="text-muted-foreground text-xs">{country.dial}</span>
            <ChevronDown size={12} className="text-muted-foreground ml-auto" />
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 z-50 mt-1 w-64 max-h-60 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                <Search size={14} className="text-muted-foreground" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search country…"
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
              <div className="overflow-y-auto max-h-48">
                {filtered.map(c => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleCountrySelect(c.code)}
                    className={cn(
                      "flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                      c.code === countryCode && "bg-accent"
                    )}
                  >
                    <span>{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-muted-foreground text-xs">{c.dial}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Number input */}
        <input
          type="tel"
          value={localNumber}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder={country.placeholder}
          className={cn(
            "flex h-12 w-full rounded-r-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm",
            error && "border-destructive focus-visible:ring-destructive"
          )}
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function validatePhone(fullPhone: string): string | null {
  if (!fullPhone) return null; // optional field
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (fullPhone.startsWith(c.dial)) {
      const local = fullPhone.slice(c.dial.length);
      if (c.pattern.test(local)) return null;
      return "Invalid mobile number for this country";
    }
  }
  return "Invalid phone number";
}
